"use client";

import { createContext, useState, useEffect, useContext } from "react";
import {
  fetchWishlist,
  addToWishlist,
  deleteFromWishlist,
} from "../services/api";
import { useAuth } from "./AuthContext";

const WishlistContext = createContext();

export const useWishlist = () => useContext(WishlistContext);

export const WishlistProvider = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      getWishlist();
    } else {
      setWishlistItems([]); // Clear wishlist when not authenticated
    }
  }, [isAuthenticated]);

  const getWishlist = async () => {
    try {
      setLoading(true);
      const data = await fetchWishlist();
      if (data && data.products) {
        setWishlistItems(data.products);
      }
    } catch (error) {
      console.error("Error fetching wishlist:", error);
    } finally {
      setLoading(false);
    }
  };

  const addItemToWishlist = async (productId) => {
    try {
      setLoading(true);
      await addToWishlist(productId);
      getWishlist(); // Refresh wishlist
    } catch (error) {
      console.error("Error adding to wishlist:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeItemFromWishlist = async (productId) => {
    try {
      setLoading(true);
      await deleteFromWishlist(productId);
      getWishlist(); // Refresh wishlist
    } catch (error) {
      console.error("Error removing from wishlist:", error);
    } finally {
      setLoading(false);
    }
  };

  const isInWishlist = (productId) => {
    return wishlistItems.some((item) => item._id === productId);
  };

  return (
    <WishlistContext.Provider
      value={{
        wishlistItems,
        loading,
        addItemToWishlist,
        removeItemFromWishlist,
        isInWishlist,
        refreshWishlist: getWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};
